import type { RJSFSchema } from '@rjsf/utils';
import { makeAutoObservable } from 'mobx';

import { IApplication, ITemplatesResponse, TFieldGroups, TSchemaTypeShape } from '@/types/applications';
import { backendApi } from '@/utils/api';
import { Api } from '@/api';
import { TemplateFieldsDto } from '@/api/tutorial/response';

export interface IApplicationsStore {
    isNewOrDraft: boolean;
	selectionId: number;
	schema: TSchemaTypeShape;
	groups: TFieldGroups;
	tutorialsFields: any;
	handleApplicationCreate: ({ data, parentId }: { data: IApplication; parentId?: number }) => void;
	handleApplicationUpdate: ({ data, id }: { data: IApplication; id: IApplication['id'] }) => void;
	handleApplicationDraftCreate: ({ data }: { data: IApplication }) => void;
	handleApplicationDraftUpdate: ({ data, id }: { data: IApplication; id: IApplication['id'] }) => void;
	handleApplicationDraftApply: ({ data, id }: { data: IApplication; id: IApplication['id'] }) => void;
	initializePageData: (selectionId: number, options?: InitializePageDataOptions) => Promise<void>;
	templateId: number;
}

type InitializePageDataOptions = {
	omitExpiredDirections?: boolean;
};

class ApplicationsStore implements IApplicationsStore {
    isNewOrDraft: boolean;
	selectionId: number;

	schema: TSchemaTypeShape;

	groups: TFieldGroups = [];

	tutorialsFields: TemplateFieldsDto;

	templateId: number;

	initializePageData = async (
		selectionId: number,
		{ omitExpiredDirections = true }: InitializePageDataOptions = {}
	) => {
		const {
			data: { id, validationSchema, schemaGroups, draftValidationSchema },
		} = await backendApi.get<{
			id: number;
			validationSchema: RJSFSchema;
			schemaGroups: TFieldGroups;
			draftValidationSchema: RJSFSchema;
		}>(`/v2/templates/validation-schema/${selectionId}?omitExpiredDirections=${omitExpiredDirections}`);

		this.schema = {
			create: validationSchema,
			draft: draftValidationSchema,
		};
		this.templateId = id;
		this.groups = ApplicationsStore.appendDirectionFields(schemaGroups, validationSchema);
		this.selectionId = selectionId;
        if (!this.isNewOrDraft) {
            this.removeLeaderEmailProperty();
        }

		const { data } = await Api.instance.tutorialApi.getTemplateFields(selectionId);
		this.tutorialsFields = data;
	};

    private removeLeaderEmailProperty() {
        if (this.schema.create?.properties?.leaderEmail) {
            delete this.schema.create.properties.leaderEmail;
        }
        if (this.schema.draft?.properties?.leaderEmail) {
            delete this.schema.draft.properties.leaderEmail;
        }
        this.schema.create.required = this.schema.create.required?.filter((field) => field !== 'leaderEmail');
        this.schema.draft.required = this.schema.draft.required?.filter((field) => field !== 'leaderEmail');
        if (!this.groups) {
            return;
        }

        for (const group of this.groups) {
            group.fields = group.fields.filter((field) => field !== 'leaderEmail');
        }
    }

	static appendDirectionFields(schemaGroups: TFieldGroups | null, validationSchema: RJSFSchema): TFieldGroups {
		if (!schemaGroups || !Object.keys(schemaGroups).length) {
			return [];
		}

		const normalizedGroups = schemaGroups.map((group) => ({
			...group,
			fields: [...new Set(group.fields)],
		}));
		const existingFields = new Set(normalizedGroups.flatMap((group) => group.fields));

		const directionOptions = {
			direction: validationSchema.definitions?.DirectionValues || {},
			subDirection: validationSchema.definitions?.SubDirectionValues || {},
			subSubDirection: validationSchema.definitions?.SubDirectionValues || {},
		};
		const directionsGroup = {
			name: '',
			fields: [] as string[],
		};

		directionsGroup.fields = Object.entries(directionOptions).reduce<string[]>((result, [dirKey, dirValues]) => {
			if (!dirValues || !Object.keys(dirValues).length || existingFields.has(dirKey)) {
				return result;
			}

			return [...result, dirKey];
		}, []);

		if (!directionsGroup.fields.length) {
			return normalizedGroups;
		}

		return [directionsGroup, ...normalizedGroups];
	}

	// FIXME: DRY
	static getShowSchema = async (selectionId: number, omitExpiredDirections: boolean = true) => {
		const { data } = await backendApi.get<{ validationSchema: RJSFSchema; schemaGroups: Record<string, string[]> }>(
			`/v2/templates/validation-schema/${selectionId}/base?omitExpiredDirections=${omitExpiredDirections}`
		);

		return data;
	};

	handleApplicationCreate = async ({ data, parentId }: { data: IApplication; parentId?: number }) => {
		await backendApi.post<ITemplatesResponse>(`/v2/applications/${this.selectionId}`, data, {
			params: {
				parentId,
			},
		});
	};

	handleApplicationDraftCreate = async ({ data }: { data: IApplication }) => {
		await backendApi.post<ITemplatesResponse>(`/v2/applications/draft/${this.selectionId}`, data);
	};

	handleApplicationDraftUpdate = async ({ data, id }: { data: IApplication; id: IApplication['id'] }) => {
		await backendApi.patch<ITemplatesResponse>(`/v2/applications/draft/${this.selectionId}/${id}`, data);
	};

	handleApplicationUpdate = async ({ data, id }: { data: IApplication; id: IApplication['id'] }) => {
		await backendApi.patch<ITemplatesResponse>(`/v2/applications/${id}/selections/${this.selectionId}`, data);
	};

	handleApplicationDraftApply = async ({ data, id }: { data: IApplication; id: IApplication['id'] }) => {
		await backendApi.post<ITemplatesResponse>(`/v2/applications/draft/${this.selectionId}/apply/${id}`, data);
	};

	static getApplication = async ({ applicationId }: { applicationId: IApplication['id'] }) => {
		await backendApi.get<ITemplatesResponse>(`/v2/applications/${applicationId}`);
	};

	static handlePassportCreate = async (projectId: IApplication['id']) => {
		const { data: passport } = await backendApi.post<ITemplatesResponse>(`/v2/project-passport/${projectId}`);

		return passport;
	};

	static handlePassportUpdate = async ({ data }: { data: IApplication }) => {
		await backendApi.patch<ITemplatesResponse>(`/v2/project-passport/${data.id}`, data);
	};

	// FIXME: need to use selection ID from current instance
	static getPassport = async ({ projectId, selectionId }: { projectId: IApplication['id']; selectionId: number }) => {
		const { data: formData } = await backendApi.get<ITemplatesResponse>(`/v2/project-passport/${projectId}`);
		const { data: schema } = await backendApi.get<ITemplatesResponse>(
			`/v2/project-passport/validation-schema/${selectionId}`
		);

		return {
			formData,
			schema,
		};
	};

	static getPassportSchema = async (projectId: IApplication['id']) => {
		await backendApi.get<ITemplatesResponse>(`/v2/project-passport/${projectId}`);
	};

	constructor(isNewOrDraft: boolean) {
        this.isNewOrDraft = isNewOrDraft;
		makeAutoObservable(this);
	}
}

export { ApplicationsStore };
